import { Api, OHIF } from '@gleamer/types';

type PredictionsApi = Api.PredictionsApi;
type UserAuthenticationService = OHIF.UserAuthenticationService;

/**
 * What the preview mode shows instead of a patient's latest predictions: one import's (the Prediction imports page),
 * or a product's current ones, of the whole patient or of one study. Never a task's.
 */
export type PredictionPreview = {
  importId?: number;
  productId?: number;
  studyInstanceUID?: string;
};

/**
 * The series one request asks about: a case's whole list in the query could pass the server's 8 KB request line
 * (~80 series), so a longer case is asked about in several requests, and their predictions merged.
 */
export const SERIES_PER_REQUEST = 40;

/** A case's series, as its predictions are read and its unsaved statuses keyed: each once, in one order. */
export function caseSeries(seriesInstanceUIDs: string[]): string[] {
  return [...new Set(seriesInstanceUIDs)].sort();
}

/** `values` in parts of `size`; one empty part for none, so a case naming no series still makes its one request. */
export function chunk<T>(values: T[], size: number): T[][] {
  const parts: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    parts.push(values.slice(i, i + size));
  }
  return parts.length ? parts : [[]];
}

export async function fetchPredictions(
  UserAuthenticationService: UserAuthenticationService,
  patientId: string,
  taskId?: number,
  preview?: PredictionPreview,
  /** A task's case: the series it shows, the only ones whose predictions are read (DATAT-662); none, every series. */
  seriesInstanceUIDs: string[] = []
): Promise<PredictionsApi> {
  if (taskId) {
    const documents = await Promise.all(
      chunk(caseSeries(seriesInstanceUIDs), SERIES_PER_REQUEST).map(part => {
        const query = new URLSearchParams();
        part.forEach(uid => query.append('seriesUid', uid));
        const suffix = query.toString() ? `?${query}` : '';
        return fetchDocument(
          UserAuthenticationService,
          patientId,
          `/api/predictions/task/${taskId}/patient/${patientId}${suffix}`
        );
      })
    );
    // one import's document, read in parts: the same algorithm and table on each part that has predictions; a single
    // part is that document
    const base = documents.find(document => document.predictions.length > 0) ?? documents[0];
    return { ...base, predictions: documents.flatMap(document => document.predictions) };
  }

  const query = new URLSearchParams();
  if (preview?.importId !== undefined) {
    query.set('importId', String(preview.importId));
  } else if (preview?.productId !== undefined) {
    query.set('productId', String(preview.productId));
  }
  if (preview?.studyInstanceUID && query.toString()) {
    query.set('studyInstanceUID', preview.studyInstanceUID);
  }
  const suffix = query.toString() ? `?${query}` : '';
  return fetchDocument(
    UserAuthenticationService,
    patientId,
    `/api/predictions/patient/${patientId}${suffix}`
  );
}

/** A patient's predictions document at `url`; none when there is none (404). */
async function fetchDocument(
  UserAuthenticationService: UserAuthenticationService,
  patientId: string,
  url: string
): Promise<PredictionsApi> {
  const response = await fetch(url, {
    method: 'GET',
    headers: new Headers(UserAuthenticationService.getAuthorizationHeader()),
  });

  if (response.status === 404) {
    console.log(`${response.status} : ${response.statusText}`);
    return {
      patientId,
      predictions: [],
    };
  }
  if (response.status >= 400) {
    throw new Error(`${response.status} : ${response.statusText}`);
  }

  return response.json();
}
